import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

import { User, EditedUser, JobRole } from '../manage-access.model';

import { MatDialog } from '@angular/material/dialog';

import { DialogAddusersRemoveComponent } from '../dialog-addusers-remove/dialog-addusers-remove.component';

import {
  validateOptionalPhoneGroup,
  validateNoRepeatingSpecialCharacters,
} from '@cigna/chcp/shared/util';

import { ManageUserAccessFacade } from '../+state/manage-user-access.facade';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'cigna-page-add-new-users',
  templateUrl: './page-add-new-users.component.html',
  styleUrls: ['./page-add-new-users.component.scss'],
})
export class PageAddNewUsersComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private manageAccessFacade: ManageUserAccessFacade,
  ) {}

  @Output() cancelFlow = new EventEmitter();

  @ViewChild('addUserBtn', { read: ElementRef, static: true })
  private addUserBtn: ElementRef;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  submitted = false;

  usersForm: FormGroup;

  jobRoles$ = this.manageAccessFacade.allJobRoles$;
  hiddenSteps$ = this.manageAccessFacade.hiddenSteps$;
  steps$ = this.manageAccessFacade.steps$;

  selectedUserSubscription: Subscription;

  ngOnInit(): void {
    this.manageAccessFacade.loadDelegationData();

    this.selectedUserSubscription = this.manageAccessFacade.selectedUsers$
      .pipe(take(1))
      .subscribe((users) => {
        if (users.length === 0) {
          this.usersForm = this.formBuilder.group({
            users: this.formBuilder.array([this.createUser(null)]),
          });
        } else {
          const existingUsers = users.map((user) => this.createUser(user));
          this.usersForm = this.formBuilder.group({
            users: this.formBuilder.array(existingUsers),
          });
        }
      });
  }

  ngOnDestroy() {
    if (this.selectedUserSubscription) {
      this.selectedUserSubscription.unsubscribe();
    }
  }

  get userList(): FormArray {
    return this.usersForm.get('users') as FormArray;
  }

  get userControls() {
    return this.userList.controls as any;
  }

  createUser(user: null | EditedUser): FormGroup {
    return this.formBuilder.group({
      firstName: [
        user ? user.firstName : '',
        [
          Validators.required,
          Validators.maxLength(30),
          // TODO: resolve CHCP disables by bot
          // eslint-disable-next-line no-useless-escape
          Validators.pattern(/^[a-zA-Z.' \-]{1,}$/),
          validateNoRepeatingSpecialCharacters,
        ],
      ],
      lastName: [
        user ? user.lastName : '',
        [
          Validators.required,
          Validators.maxLength(50),
          // TODO: resolve CHCP disables by bot
          // eslint-disable-next-line no-useless-escape
          Validators.pattern(/^[a-zA-Z.' \-]{1,}$/),
          validateNoRepeatingSpecialCharacters,
        ],
      ],
      jobRole: [user ? user.jobRoleId : '', Validators.required],
      phone: this.formBuilder.group(
        {
          phone1: [
            user && user.phone && user.phone !== ''
              ? user.phone.substring(0, 3)
              : '',
            [
              Validators.pattern(/^[0-9]*$/),
              Validators.minLength(3),
              Validators.maxLength(3),
            ],
          ],
          phone2: [
            user && user.phone && user.phone !== ''
              ? user.phone.substring(3, 6)
              : '',
            [
              Validators.pattern(/^[0-9]*$/),
              Validators.minLength(3),
              Validators.maxLength(3),
            ],
          ],
          phone3: [
            user && user.phone && user.phone !== ''
              ? user.phone.substring(6, 10)
              : '',
            [
              Validators.pattern(/^[0-9]*$/),
              Validators.minLength(3),
              Validators.maxLength(4),
            ],
          ],
        },
        { validator: validateOptionalPhoneGroup },
      ),
      phoneExt: [
        user ? user.phoneExt : '',
        [
          Validators.pattern(/^[a-zA-Z0-9]*$/),
          Validators.minLength(0),
          Validators.maxLength(7),
        ],
      ],
      email: [user ? user.email : '', [Validators.email, Validators.required]],
    });
  }

  addUser() {
    const control = this.usersForm.controls['users'] as FormArray;
    control.push(this.createUser(null));
    setTimeout(() => {
      this.addUserBtn.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
  }

  removeUser(i: number) {
    const dialogRef = this.dialog.open(DialogAddusersRemoveComponent, {
      data: {
        index: i,
      },
      autoFocus: false,
      disableClose: true,
      width: '600px',
    });

    dialogRef.componentInstance.removeUserFromAdd.subscribe((index: number) => {
      const control = this.usersForm.controls['users'] as FormArray;
      control.removeAt(index);
      dialogRef.close();
    });
  }

  mapFormUsersToState(users: any, jobRoles: JobRole[]): User[] {
    return users.map((input: any, index: number) => ({
      id: `__new__${index}`,
      firstName: input.firstName,
      lastName: input.lastName,
      jobRoleId: input.jobRole,
      jobRole: jobRoles.find((role) => role.id === input.jobRole)?.name,
      email: input.email,
      phone: input.phone.phone1 + input.phone.phone2 + input.phone.phone3,
      phoneExt: input.phoneExt,
    }));
  }

  nextScreen(jobRoles: JobRole[]): void {
    if (this.usersForm.valid) {
      this.manageAccessFacade.addUsersAndStartAssignment(
        this.mapFormUsersToState(this.usersForm.value.users, jobRoles),
      );
    } else {
      this.submitted = true;
      this.usersForm.markAllAsTouched();
    }
  }
}
